import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  MapPinIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import {
  deleteGeschaeftspartnerHeroObjektadresse,
  deleteGeschaeftspartnerObjektadresse,
  formatObjektadresseLine,
  kundeOrteExportDownloadUrl,
  listGeschaeftspartnerObjektadressen,
  type Objektadresse,
} from "@/lib/kontakte";
import { useRealtimeSync } from "@/lib/useRealtime";
import { ObjektadresseFormDialog } from "@/pages/kontakte/ObjektadresseFormDialog";
import { ROW_ACTIONS_HEADER_CLASS, TableRowActions } from "@/pages/kontakte/TableRowActions";

interface ObjektadressenSectionProps {
  heroContactId?: number | null;
  geschaeftspartnerId?: number | null;
}

export function ObjektadressenSection({
  heroContactId = null,
  geschaeftspartnerId = null,
}: ObjektadressenSectionProps) {
  const [rows, setRows] = useState<Objektadresse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<Objektadresse | null>(null);

  const load = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!geschaeftspartnerId && !heroContactId) {
        setRows([]);
        setLoading(false);
        setError(null);
        return;
      }
      if (!geschaeftspartnerId) {
        setRows([]);
        setLoading(false);
        setError(null);
        return;
      }
      if (!options?.silent) setLoading(true);
      setError(null);
      try {
        const list = await listGeschaeftspartnerObjektadressen(geschaeftspartnerId);
        setRows(list || []);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Objektadressen konnten nicht geladen werden.",
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [geschaeftspartnerId, heroContactId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: ["kontakte"],
    paused: formOpen,
    enabled: geschaeftspartnerId != null,
    onChange: () => void load({ silent: true }),
  });

  function openCreate() {
    setEditRow(null);
    setFormOpen(true);
  }

  function openEdit(addr: Objektadresse) {
    setEditRow(addr);
    setFormOpen(true);
  }

  async function handleDelete(addr: Objektadresse) {
    if (geschaeftspartnerId == null) return;
    if (!window.confirm(`Objektadresse „${addr.title || formatObjektadresseLine(addr)}“ wirklich löschen?`)) {
      return;
    }
    try {
      if (addr.ort_id != null) {
        await deleteGeschaeftspartnerObjektadresse(geschaeftspartnerId, addr.ort_id);
      } else {
        await deleteGeschaeftspartnerHeroObjektadresse(geschaeftspartnerId, addr.address_id);
      }
      await load({ silent: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Objektadresse konnte nicht gelöscht werden.",
      );
    }
  }

  const columns = useMemo(
    () => [
      {
        key: "title",
        header: "Bezeichnung",
        sortable: true,
        sortValue: (addr: Objektadresse) => addr.title || "",
        render: (addr: Objektadresse) => (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-label">
              {addr.title || <span className="text-label-tertiary">—</span>}
            </span>
            {addr.editable === false ? (
              <span className="inline-flex rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                aus Hero
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: "adresse",
        header: "Adresse",
        sortable: true,
        sortValue: (addr: Objektadresse) => formatObjektadresseLine(addr),
        render: (addr: Objektadresse) => formatObjektadresseLine(addr),
      },
      {
        key: "maps",
        header: "",
        headerClassName: ROW_ACTIONS_HEADER_CLASS.three,
        render: (addr: Objektadresse) => (
          <TableRowActions
            onEdit={addr.editable !== false ? () => openEdit(addr) : undefined}
            onDelete={addr.editable !== false ? () => void handleDelete(addr) : undefined}
            afterEdit={
              addr.maps_link ? (
                <a
                  href={addr.maps_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="eh-data-action-btn"
                  title="In Karte öffnen"
                  aria-label="In Karte öffnen"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ArrowTopRightOnSquareIcon className="h-[18px] w-[18px]" aria-hidden />
                </a>
              ) : null
            }
          />
        ),
      },
    ],
    [geschaeftspartnerId],
  );

  return (
    <Card
      title="Objektadressen"
      description={
        rows.length > 0
          ? `${rows.length.toLocaleString("de-DE")} ${rows.length === 1 ? "Standort" : "Standorte"} – Einsatzorte für Projekte, Wartungen und Lieferscheine.`
          : "Einsatzorte dieses Kontakts für Projekte, Wartungen und Lieferscheine."
      }
      actions={
        <div className="flex flex-wrap gap-2">
          {geschaeftspartnerId != null ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                window.open(kundeOrteExportDownloadUrl(geschaeftspartnerId), "_blank", "noopener")
              }
              leftIcon={<ArrowDownTrayIcon className="h-4 w-4" aria-hidden />}
            >
              Excel-Export
            </Button>
          ) : null}
          {geschaeftspartnerId != null ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={openCreate}
              leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
            >
              Objektadresse anlegen
            </Button>
          ) : null}
        </div>
      }
    >
      {error ? (
        <Alert variant="error" title="Fehler" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<MapPinIcon />}
          title="Noch keine Objektadressen"
          description="Hinterlege Standorte, an denen für diesen Kontakt gearbeitet wird. Adressen aus Hero erscheinen hier automatisch."
          action={
            geschaeftspartnerId != null ? (
              <Button size="sm" onClick={openCreate} leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}>
                Objektadresse anlegen
              </Button>
            ) : null
          }
        />
      ) : (
        <Table
          columns={columns}
          rows={rows}
          rowKey={(addr) =>
            addr.ort_id != null ? `ort-${addr.ort_id}` : `addr-${addr.address_id}`
          }
          rowClassName={() => "align-middle"}
          stickyEndColumnCount={1}
        />
      )}

      {geschaeftspartnerId != null ? (
        <ObjektadresseFormDialog
          open={formOpen}
          geschaeftspartnerId={geschaeftspartnerId}
          initial={editRow}
          onClose={() => {
            setFormOpen(false);
            setEditRow(null);
          }}
          onSaved={() => {
            void load({ silent: true });
          }}
        />
      ) : null}
    </Card>
  );
}
